import { Box, Button, Chip, Grid, Paper, Typography } from '@mui/material';
import type React from 'react';
import { BaseModal } from '../../common/UI/BaseModal';
import type { RecentActivity } from './types';

interface ActivityDetailsModalProps {
  open: boolean;
  onClose: () => void;
  selectedActivity: RecentActivity | null;
  formatTimestamp: (timestamp: string) => string;
  getUserLabel: (userId?: string) => string;
  getActivityTypeColor: (
    type: string
  ) => 'success' | 'info' | 'warning' | 'secondary' | 'primary' | 'error' | 'default';
}

export const ActivityDetailsModal: React.FC<ActivityDetailsModalProps> = ({
  open,
  onClose,
  selectedActivity,
  formatTimestamp,
  getUserLabel,
  getActivityTypeColor,
}) => {
  return (
    <BaseModal
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      title={
        <>
          Activity Details
          {selectedActivity && (
            <Chip
              label={selectedActivity.activityType}
              size="small"
              color={getActivityTypeColor(selectedActivity.activityType)}
              sx={{ ml: 2 }}
            />
          )}
        </>
      }
      actions={<Button onClick={onClose}>Close</Button>}
    >
      {selectedActivity && (
        <Box>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Timestamp
              </Typography>
              <Typography variant="body1" gutterBottom>
                {formatTimestamp(selectedActivity.timestamp)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2" color="text.secondary">
                User
              </Typography>
              <Typography variant="body1" gutterBottom>
                {getUserLabel(selectedActivity.userId)} ({selectedActivity.userRole})
              </Typography>
            </Grid>
            {selectedActivity.clientInfo && (
              <>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    IP Address
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedActivity.clientInfo.ip || 'N/A'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    User Agent
                  </Typography>
                  <Typography
                    variant="body2"
                    gutterBottom
                    sx={{
                      fontFamily: 'monospace',
                      bgcolor: 'action.hover',
                      p: 1,
                      borderRadius: 1,
                    }}
                  >
                    {selectedActivity.clientInfo.userAgent || 'N/A'}
                  </Typography>
                </Grid>
              </>
            )}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Full Data Payload
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: 'greyLight',
                  color: 'text.primary',
                  maxHeight: '300px',
                  overflow: 'auto',
                }}
              >
                <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                  {JSON.stringify(selectedActivity.data, null, 2)}
                </pre>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}
    </BaseModal>
  );
};
